export const STATUS_LABEL = {
  utkast: "Påbegynt",
  new: "Ny forespørsel",
  draft: "Under arbeid",
  calculating: "Kalkuleres",
  quoted: "Tilbud skrevet",
  sent: "Tilbud sendt",
  negotiation: "Forhandling",
  won: "Vunnet",
  lost: "Tapt",
  declined: "Gir ikke tilbud",
  forkastet: "Forkastet",
};

export const STATUS_TONE = {
  utkast: "border border-dashed border-[hsl(var(--brand)/0.35)] bg-white text-[hsl(var(--brand-strong))]",
  new: "bg-[hsl(var(--brand)/0.08)] text-[hsl(var(--brand-strong))]",
  draft: "bg-black/[0.05] text-foreground/70",
  calculating: "bg-[hsl(35_80%_48%/0.14)] text-[hsl(35_80%_30%)]",
  quoted: "bg-[hsl(var(--brand-soft))] text-[hsl(208_90%_34%)]",
  sent: "bg-[hsl(var(--brand-soft))] text-[hsl(208_90%_34%)]",
  negotiation: "bg-[hsl(268_35%_48%/0.12)] text-[hsl(268_35%_36%)]",
  won: "bg-[hsl(152_50%_36%/0.14)] text-[hsl(152_50%_24%)]",
  lost: "bg-[hsl(0_50%_46%/0.1)] text-[hsl(0_50%_38%)]",
  declined: "bg-black/[0.05] text-muted-foreground",
  forkastet: "bg-black/[0.04] text-muted-foreground",
};

export const OPEN_STATUSES = ["draft", "calculating", "quoted", "sent", "negotiation"];
export const CLOSED_STATUSES = ["won", "lost", "declined"];
export const isDraftCase = (p) => p?.status === "utkast" || p?.status === "forkastet";

/** Honest sum for a case in a list: «Ikke beregnet» · «Foreløpig …» · the ordinary sum. 0 kr never stands in for «unknown». */
export function caseSum(p) {
  const c = p?.calc;
  if (!c || c.sum_kind === "none") return { kind: "none", text: "Ikke beregnet" };
  const text = money(c.price, "NOK", 1, { bare: true });
  if (c.sum_kind === "preliminary") return { kind: "preliminary", text, sub: c.coverage?.total ? `${c.coverage.priced} av ${c.coverage.total} priset` : "foreløpig" };
  return { kind: "complete", text };
}

/** The one thing to do next on a case – shown in the work list, opens the right tab. */
export function nextAction(p) {
  const c = p?.calc || {};
  const q = p?.quote;
  switch (p?.status) {
    case "utkast": return { key: "confirm", label: "Opprett arbeid", tab: "oversikt", tone: "brand" };
    case "forkastet": return { key: "restore", label: "Gjenopprett", tab: "oversikt", tone: "muted" };
    case "won": case "lost": case "declined": return null;
    default: break;
  }
  if (p?.reading === "running") return { key: "reading", label: "Leser dokumenter…", tab: "krav", tone: "muted" };
  if (p?.open_clarifications) return { key: "clarify", label: `${p.open_clarifications} ${p.open_clarifications === 1 ? "avklaring" : "avklaringer"}`, tab: "krav", tone: "warn" };
  if (c.attention) return { key: "attention", label: `${c.attention} ${c.attention === 1 ? "linje å vurdere" : "linjer å vurdere"}`, tab: "kalkyle", tone: "warn" };
  if (!c.customer_items) return { key: "items", label: "Legg inn arbeidsposter", tab: "krav", tone: "muted" };
  if (c.sum_kind === "none") return { key: "price", label: "Start kalkylen", tab: "kalkyle", tone: "brand" };
  if (c.sum_kind === "preliminary") { const n = c.missing_count ?? Math.max(0, (c.coverage?.total || 0) - (c.coverage?.priced || 0)); return { key: "price", label: n ? `Pris ${n} ${n === 1 ? "post" : "poster"}` : "Fullfør kalkylen", tab: "kalkyle", tone: "brand" }; }
  if (!q) return { key: "revision", label: "Lag tilbudsversjon", tab: "tilbud", tone: "brand" };
  if (q.stale) return { key: "revision", label: "Kalkylen er oppdatert – oppdater tilbudet", tab: "tilbud", tone: "warn" };
  if (p?.risk && !p.risk.ok && p.risk.required !== false) return { key: "risk", label: p.risk.required === null ? "Risiko: terskel uavklart" : p.risk.exists ? (p.risk.stale && p.risk.complete ? "Bekreft risikovurderingen" : "Fullfør risikosjekklisten") : "Fyll ut risikosjekklisten", tab: "tilbud", tone: "warn" };
  if (q.status === "utkast") return { key: "control", label: "Kontroller tilbudet", tab: "tilbud", tone: "brand" };
  return { key: "ready", label: "Tilbudet er kontrollert", tab: "tilbud", tone: "ok" };
}

/** The one calm phrase in the case header: where the case is, in Egil's words – never «Tilbud skrevet» while nothing is priced. */
export function phaseLabel(p, ctx) {
  const s = p?.status;
  const t = ctx?.totals || {};
  const q = ctx?.quote;
  if (!s || s === "utkast" || s === "forkastet" || ["new", "sent", "negotiation", "won", "lost", "declined"].includes(s)) return STATUS_LABEL[s] || "";
  if (!t.customer_items || t.sum_kind !== "complete") return "Under arbeid";
  if (q && !q.stale && q.status === "kontrollert") return "Tilbud kontrollert";
  if (q && !q.stale) return "Tilbud klart til kontroll";
  return "Kalkyle ferdig";
}

/** Days until an ISO date (negative = passed), or null. */
export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

/* ---- LOS domain: work items, cost elements, documents ---- */
export const PRICE_TYPE_LABEL = { fastpris: "Fastpris", estimat: "Estimat", enhetspris: "Enhetspris", etter_regning: "Etter regning", inkludert: "Inkludert i annen post", opsjon: "Opsjon", utgar: "Utgår / ikke tilbudt" };
/** Short forms for table cells – the full wording lives in the picker. */
export const PRICE_TYPE_SHORT = { ...PRICE_TYPE_LABEL, inkludert: "Inkludert", utgar: "Utgår" };
export const PRICE_TYPE_OPTIONS = Object.entries(PRICE_TYPE_LABEL);
export const ITEM_STATUS_LABEL = { ny: "Ny", klar: "Klar", avklares: "Avklares", opsjon: "Opsjon", ikke_tilbudt: "Ikke tilbudt" };
export const ITEM_STATUS_OPTIONS = Object.entries(ITEM_STATUS_LABEL);
export const ITEM_STATE = {
  klar: { label: "Klar", cls: "v-ok" },
  venter_pris: { label: "Ikke priset", cls: "bg-black/[0.05] text-foreground/65", quiet: true },
  ma_vurderes: { label: "Må vurderes", cls: "bg-black/[0.05] text-foreground/65", quiet: true },
  inkludert: { label: "Inkludert", cls: "bg-black/[0.05] text-foreground/60", quiet: true },
  utgar: { label: "Utgår", cls: "bg-black/[0.05] text-muted-foreground line-through", quiet: true },
};
/** «Ikke priset» is calm work status (plain text); amber badges are reserved for things that need a decision: missing quantity, conflicting sources, open questions. */
export const REASON_BADGE = {
  qty_missing: { label: "Mangler mengde", cls: "v-assumed" },
  conflict: { label: "Motstridende kilder", cls: "v-assumed" },
  open_clarifications: { label: (n) => `${n} ${n === 1 ? "avklaring" : "avklaringer"}`, cls: "v-assumed" },
  no_price_type: { label: "Ikke vurdert", cls: "bg-black/[0.05] text-foreground/65", quiet: true },
  no_elements: { label: "Ikke priset", cls: "bg-black/[0.05] text-foreground/65", quiet: true },
  no_price: { label: "Ikke priset", cls: "bg-black/[0.05] text-foreground/65", quiet: true },
  not_done: { label: "Ikke vurdert ferdig", cls: "bg-black/[0.05] text-foreground/65", quiet: true },
};
export const STATE_ORDER = ["ma_vurderes", "venter_pris", "klar", "inkludert", "utgar"];
export const COST_KIND_LABEL = { arbeid: "Arbeid", materiell: "Materiell", utstyr: "Utstyr", underleverandor: "Underleverandør", annet: "Annet" };
export const COST_KIND_OPTIONS = Object.entries(COST_KIND_LABEL);
export const PRICING_METHOD_LABEL = { db: "DB %", paslag: "Påslag %", pris: "Salgspris", manuell: "Manuell" };
export const PRICING_METHOD_OPTIONS = Object.entries(PRICING_METHOD_LABEL);
export const DOC_ROLE = {
  foresporsel: { n: "01", label: "Forespørsel" },
  kalkyle_tilbud: { n: "02", label: "Kalkulasjon / tilbud" },
  underleverandor: { n: "03", label: "Underleverandører" },
  tegning_dok: { n: "04", label: "Tegninger / dokumentasjon" },
  korrespondanse: { n: "05", label: "Korrespondanse" },
  kontrakt: { n: "06", label: "Kontraktsdokument" },
  ukjent: { n: "–", label: "Ukategorisert" },
};
export const DOC_ROLE_ORDER = ["foresporsel", "kalkyle_tilbud", "underleverandor", "tegning_dok", "korrespondanse", "kontrakt", "ukjent"];
export const DOC_ROLE_OPTIONS = DOC_ROLE_ORDER.map((k) => [k, `${DOC_ROLE[k].n} ${DOC_ROLE[k].label}`.replace("– ", "")]);
export const DOC_STATUS = {
  mottatt: { label: "Mottatt", cls: "bg-black/[0.05] text-foreground/60" },
  lest: { label: "Lest", cls: "v-ok" },
  delvis: { label: "Delvis lest", cls: "v-assumed" },
  manuell: { label: "Manuell vurdering", cls: "v-eng" },
  duplikat: { label: "Duplikat", cls: "bg-black/[0.04] text-muted-foreground" },
};
export const ASSUMPTION_STATUS = { dekket: "Dekket i kalkylen", kunde: "Leveres av kunden", inkludert: "Inkludert under annen post", avgrenset: "Avgrenset", uavklart: "Uavklart" };
export const PARTY_LABEL = { los: "Skarpenord", kunde: "Kunde", underleverandor: "Underleverandør" };
export const CLAR_KIND_LABEL = { kunde: "Kunde", intern: "Intern", leverandor: "Leverandør" };

export const VALIDATION = {
  validated: { label: "Teknisk validert", cls: "v-ok" },
  exception: { label: "Godkjent med dokumentert unntak", cls: "v-ok" },
  assumed: { label: "Antatt – må bekreftes", cls: "v-assumed" },
  needs_engineer: { label: "Krever ingeniørvurdering", cls: "v-eng" },
  blocked: { label: "Blokkert", cls: "v-blocked" },
};
/** Three different acts – never one «unntak»: ordinary approval · manual verification against a named document · an accepted requirement deviation. */
export const APPROVAL_KIND = {
  teknisk: { label: "Teknisk godkjent", short: "Teknisk", verb: "Godkjenn teknisk" },
  verifisert: { label: "Manuelt verifisert mot dokumentasjon", short: "Verifisert", verb: "Registrer manuell verifikasjon" },
  kravavvik: { label: "Godkjent kravavvik (kundeaksept)", short: "Kravavvik", verb: "Registrer kravavvik" },
  unntak: { label: "Godkjent som unntak (eldre)", short: "Unntak", verb: "Godkjenn som unntak" },
};

export const VALVE_STATUS = {
  mapped: { label: "Kartlagt", cls: "v-ok" },
  needs_clarification: { label: "Må avklares", cls: "v-assumed" },
  blocked: { label: "Blokkert", cls: "v-blocked" },
};

export const FAIL_LABEL = { FC: "Fail close", FO: "Fail open", FL: "Fail last" };
export const TYPE_LABEL = { ball: "Kule", butterfly: "Spjeld", globe: "Globe", gate: "Sluse", plug: "Plugg", check: "Tilbakeslag", other: "Ukjent" };
/** The configurator's answer per valve – three words a person can trust, plus the one only a person can give. */
export const PROPOSAL_STATUS = {
  teknisk_godkjent: { label: "Teknisk godkjent", short: "Godkjent", cls: "v-ok", dot: "bg-[hsl(var(--v-ok))]" },
  unntak_godkjent: { label: "Godkjent med dokumentert unntak", short: "Unntak", cls: "v-ok", dot: "bg-[hsl(var(--v-ok))]" },
  foreslatt: { label: "Foreslått", short: "Foreslått", cls: "v-ok", dot: "bg-[hsl(var(--brand))]" },
  krever_avklaring: { label: "Krever avklaring", short: "Avklaring", cls: "v-assumed", dot: "bg-[hsl(var(--v-assumed))]" },
  krever_ingenior: { label: "Krever ingeniør", short: "Ingeniør", cls: "v-eng", dot: "bg-[hsl(var(--v-eng))]" },
  blokkert: { label: "Mangler data", short: "Mangler data", cls: "v-blocked", dot: "bg-[hsl(var(--v-blocked))]" },
};
export const PROPOSAL_ORDER = ["teknisk_godkjent", "unntak_godkjent", "foreslatt", "krever_avklaring", "krever_ingenior", "blokkert"];
export const APPROVED = ["teknisk_godkjent", "unntak_godkjent"];
/** Oversikt │ Arbeidsomfang · Ventiler · Konfigurasjon · Kalkyle · Tilbud – context, then the production flow. `group` draws the quiet divider between them. */
export const TABS = [
  { key: "oversikt", label: "Oversikt", short: "Oversikt", group: "kontekst" },
  { key: "krav", label: "Omfang", short: "Omfang", group: "arbeid" },
  { key: "ventiler", label: "Ventiler", short: "Ventiler", group: "arbeid" },
  { key: "konfig", label: "Konfigurasjon", short: "Konfig", group: "arbeid" },
  { key: "kalkyle", label: "Kalkyle", short: "Kalkyle", group: "arbeid" },
  { key: "tilbud", label: "Tilbud", short: "Tilbud", group: "arbeid" },
  { key: "meldinger", label: "Meldinger", short: "Meldinger", group: "meldinger" },
];

/** Provenance of a case fact – a finding, a proposal or a confirmed value are three different things. */
export const BASIS = {
  bruker: { label: "Bekreftet", short: "Bekreftet", cls: "text-[hsl(var(--v-ok))]" },
  dokument: { label: "Fra dokument – ikke bekreftet", short: "Fra dokument", cls: "text-muted-foreground" },
  ki: { label: "KI-forslag – må bekreftes", short: "KI-forslag", cls: "text-[hsl(var(--v-assumed))]" },
};
export const basisOf = (project, key) => (project?.facts_basis || {})[key]?.basis || null;

/** In-app source viewer URL – deterministic at click time (page / sheet+cell in the query), so the exact spot is openable and inspectable. */
export function sourceHref(caseId, docId, where) {
  const q = new URLSearchParams();
  if (where?.page) q.set("page", String(where.page));
  if (where?.sheet) q.set("sheet", where.sheet);
  if (where?.row) q.set("row", String(where.row));
  if (where?.cell) q.set("cell", where.cell);
  (where?.q || []).filter(Boolean).slice(0, 2).forEach((t) => q.append("q", String(t).slice(0, 300)));
  const s = q.toString();
  return `/p/${caseId}/kilde/${docId}${s ? `?${s}` : ""}`;
}

/** Short, readable source label: «Spesifikasjon · s. 7», «Svarark · E6». */
export function sourceLabel(where, kind) {
  if (where?.page) return `${kind === "spec" ? "Spesifikasjon" : "Kilde"} · s. ${where.page}`;
  if (where?.cell) return `Svarark · ${where.cell}`;
  if (where?.row) return `Svarark · rad ${where.row}`;
  return null;
}

export function money(value, currency = "NOK", rate = 1, opts = {}) {
  const n = (Number(value) || 0) / (rate || 1);
  const digits = opts.digits ?? 0;
  const s = new Intl.NumberFormat("nb-NO", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n).replace(/\u00a0/g, " ");
  return opts.bare ? s : `${s} ${currency}`;
}

export function compactMoney(value, currency = "NOK", rate = 1) {
  const n = (Number(value) || 0) / (rate || 1);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} M${currency}`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1000).toLocaleString("nb-NO")} k${currency}`;
  return `${Math.round(n).toLocaleString("nb-NO")} ${currency}`;
}

export function num(n, digits = 0) {
  if (n === null || n === undefined || n === "") return "–";
  return new Intl.NumberFormat("nb-NO", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(n)).replace(/\u00a0/g, " ");
}

export function ago(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "akkurat nå";
  if (diff < 3600) return `for ${Math.round(diff / 60)} min siden`;
  if (diff < 86400) return `for ${Math.round(diff / 3600)} t siden`;
  if (diff < 86400 * 2) return "i går";
  if (diff < 86400 * 7) return `for ${Math.round(diff / 86400)} dager siden`;
  return d.toLocaleDateString("nb-NO", { day: "numeric", month: "short" });
}

export function longDate(iso, locale = "nb-NO") {
  if (!iso) return "–";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
}

export function lineTotal(it) {
  return (Number(it.qty) || 0) * (Number(it.sales_price) || 0) * (1 - (Number(it.discount_pct) || 0) / 100);
}

export function lineCost(it) {
  return (Number(it.qty) || 0) * (Number(it.cost_price) || 0);
}

export function markupPct(it) {
  const c = Number(it.cost_price) || 0;
  const s = Number(it.sales_price) || 0;
  return c ? ((s - c) / c) * 100 : 0;
}
